import { createContext } from '@lit/context';
import { getElementOffset } from '../ui/dom-helpers';
import { html, nothing, render } from 'lit';

export class ImagePreview {
  private imgPreview: HTMLDivElement = document.createElement('DIV') as HTMLDivElement;

  /**
   * Pending deferred-hide timer. The big preview panel overlaps its trigger thumbnail, so moving the cursor
   * from the thumbnail onto the panel fires the thumbnail's mouseleave. Hiding immediately then would remove
   * the panel out from under the cursor - which lands back on the thumbnail, re-shows the panel, and loops
   * (a flicker storm). Instead the panel keeps itself open while hovered and hides only after a short grace
   * period, so the panel stays solid whenever the cursor is over EITHER the thumbnail or the panel, and
   * disappears only once it has left both.
   */
  private hideTimer: ReturnType<typeof setTimeout> | null = null;

  private cancelPendingHide() {
    if (this.hideTimer !== null) {
      clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
  }

  showImagePreview(imgSrc: string, classes?: string) {
    this.cancelPendingHide();
    const panel = document.body;
    const template = () => {
      return html`
        <div
          class="m-3 p-3 border border-1 position-fixed image-preview-context ${classes ?? nothing}"
          @mouseenter=${() => this.cancelPendingHide()}
          @mouseleave=${() => this.hideImagePreview()}
        >
          <img class="" src=${imgSrc} alt="A big preview" />
        </div>
      `;
    };
    render(template(), this.imgPreview);

    if (!panel) return;
    const offset = getElementOffset(panel);
    const clientHeight = panel.clientHeight / 100;
    const clientWidth = panel.clientWidth / 100;
    const div = this.imgPreview.querySelector('div');
    if (div) {
      div.style.left = `${200}px`;
      div.style.top = `${offset.top + clientHeight * 10}px`;
      div.style.width = `${clientWidth * 100 - 240}px`;
      div.style.height = `${clientHeight * 80}px`;
    }
    if (!this.imgPreview.parentNode) {
      document.body.appendChild(this.imgPreview);
    }
    this.imgPreview.style.display = '';
  }

  hideImagePreview() {
    // Deferred so the cursor can travel between the thumbnail and the (overlapping) panel without the panel
    // being pulled out from under it. A mouseenter on either the thumbnail or the panel cancels this via
    // showImagePreview / cancelPendingHide before it fires; only a genuine departure from both lets it run.
    this.cancelPendingHide();
    this.hideTimer = setTimeout(() => {
      this.hideTimer = null;
      this.imgPreview.style.display = 'none';
    }, 150);
  }
}

export const imagePreviewContext = createContext<ImagePreview>('imagepreview');
